// Rendering helpers for an Algolia search hit, shared by both engines: the Nuxt
// SearchAlgolia.vue / PageSearch.vue and the Astro SearchResults.astro / TheSearch.astro.
// Both build result cards from index data and hand it to v-html or innerHTML, so these
// escapes are what stands between the index and script execution.

export interface SearchHit {
  title?: string
  field_wysiwyg?: string
  url?: string
  uri?: string
  changed?: number
  content_type?: string
  computed_cloudinary_page?: string
  _highlightResult?: Record<string, { value?: string } | undefined>
}

export function escapeHtml(value: string | null | undefined): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function escapeAttr(value: string | null | undefined): string {
  return escapeHtml(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Strip the CMS origin and Drupal's `internal:` prefix so a hit URL resolves on this site.
 * Scheme and leading `//` are both optional in the match: the index records `http://cms.…`
 * and occasionally `//cms.…` while `cmsBasePath` is configured as `https://cms.…`, and an
 * exact match leaves every result link pointing at the CMS host.
 */
export function cleanUrl(url: string | undefined, cmsBasePath = ''): string {
  if (!url) return '#'
  const cleaned = url.replace(/^internal:/, '')
  if (!cmsBasePath) return cleaned

  // Anchored, or the host would also be rewritten where it appears as a query value,
  // turning `/x?next=https://cms.…/y` into `/x?next=/y`. The trailing boundary keeps it
  // from matching a host that merely starts with ours: without it
  // `https://cms.djurensratt.se.evil/x` becomes `.evil/x`, which then reads as a path.
  const host = cmsBasePath.replace(/^(?:https?:)?\/\//, '')
  const origin = new RegExp(`^(?:https?:)?(?:\\/\\/)?${escapeRegExp(host)}(?=[/?#]|$)`, 'i')
  return cleaned.replace(origin, '')
}

/**
 * `cleanUrl` plus a scheme allowlist. `javascript:` and friends collapse to `#`, and so
 * does a protocol-relative `//host/x`, which would otherwise inherit the page scheme and
 * silently send a visitor off-site.
 *
 * Not HTML-escaped: this is the form for a framework binding (Vue `:to`, `:href`), which
 * sets the attribute directly. Escaping there would turn a `&` in a query string into a
 * literal `&amp;`.
 */
export function safeUrl(url: string | undefined, cmsBasePath = ''): string {
  const cleaned = cleanUrl(url, cmsBasePath)
  // Browsers read a backslash as a slash in the authority position, so `/\host` and
  // `\/host` leave the site exactly as `//host` does.
  if (/^[/\\]{2}/.test(cleaned)) return '#'
  if (!/^(https?:|\/|#|\.|mailto:|tel:)/i.test(cleaned)) return '#'
  return cleaned
}

/** `safeUrl`, attribute-escaped — the form for interpolating into an HTML string. */
export function safeHref(url: string | undefined, cmsBasePath = ''): string {
  return escapeAttr(safeUrl(url, cmsBasePath))
}

// Algolia returns `_highlightResult.value` verbatim, markup included. Escape must run
// before the restore, or escaped content could smuggle a real tag through it. Both tag
// names are restored because the two engines configure different ones.
const HIGHLIGHT_TAGS = /&lt;(\/?)(mark|em)&gt;/g

export function renderHighlight(value: string): string {
  return escapeHtml(value).replace(HIGHLIGHT_TAGS, '<$1$2>')
}

/** The highlighted field when Algolia matched it, else the raw field — escaped either way. */
/**
 * The highlighted field when Algolia matched it, else the raw field.
 *
 * `preEscaped` is which client produced the hit, and getting it wrong is visible either
 * way. InstantSearch (`ais-hits`) already HTML-escapes `_highlightResult` values and
 * inserts raw highlight tags, so escaping again renders a literal `&amp;` on the page.
 * The plain Algolia client escapes nothing, so not escaping puts index markup in the DOM.
 *
 * The unhighlighted fallback is a raw index attribute under both clients, so it is always
 * escaped — that branch is the one the Nuxt dropdown used to render unescaped.
 */
export function hitField(
  hit: SearchHit,
  field: string,
  { preEscaped = false }: { preEscaped?: boolean } = {},
): string {
  const highlight = hit._highlightResult?.[field]?.value
  if (highlight) return preEscaped ? highlight : renderHighlight(highlight)
  return escapeHtml((hit as Record<string, unknown>)[field] as string | undefined)
}

export const hitTitle = (hit: SearchHit) => hitField(hit, 'title')
export const hitBody = (hit: SearchHit) => hitField(hit, 'field_wysiwyg')

// Algolia `changed` is unix seconds. The card shows a calendar day, so it has to be
// resolved in Stockholm time — toISOString() is UTC and renders an evening edit as
// yesterday. Callers render inside a map, so an unparseable value must not throw.
const STOCKHOLM_DAY = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Stockholm',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000)
  if (Number.isNaN(date.getTime())) return ''
  const parts = STOCKHOLM_DAY.formatToParts(date)
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

type ImageOptions = { cloudName?: string; cmsBasePath?: string }

// The result card changes shape per breakpoint (3:2, then 2:1, then 5:2), so images are
// resized by width only and cropped by object-cover.
const SEARCH_WIDTHS = [480, 800, 1200, 1600]

/** `sizes` for a search result image: full width below lg, half the 1362px container above. */
export const SEARCH_IMAGE_SIZES = '(min-width: 1362px) 650px, (min-width: 1024px) 50vw, 100vw'

export function getImageUrl(hit: SearchHit, options: ImageOptions = {}): string | null {
  return searchImageAt(hit, options, 800)
}

export function getImageSrcset(hit: SearchHit, options: ImageOptions = {}): string | null {
  if (!options.cloudName || !searchImageAt(hit, options, SEARCH_WIDTHS[0]!)) return null
  return SEARCH_WIDTHS.map((width) => `${searchImageAt(hit, options, width)} ${width}w`).join(', ')
}

function searchImageAt(
  hit: SearchHit,
  { cloudName = '', cmsBasePath = '' }: ImageOptions,
  width: number,
): string | null {
  const transform = `w_${width},c_limit,f_auto,q_auto`
  if (hit.computed_cloudinary_page && cloudName) {
    return `https://res.cloudinary.com/${cloudName}/image/upload/${transform}/${hit.computed_cloudinary_page}`
  }
  const original = !hit.uri
    ? null
    : /^https?:\/\//i.test(hit.uri)
      ? hit.uri
      : cmsBasePath
        ? cmsBasePath + hit.uri
        : null
  if (!original) return null
  // Legacy originals are full-size files (several MB), so they are fetched through
  // Cloudinary at card size rather than linked directly.
  return cloudName ? `https://res.cloudinary.com/${cloudName}/image/fetch/${transform}/${encodeURI(safeDecodeURI(original))}` : original
}

function safeDecodeURI(url: string): string {
  try {
    return decodeURI(url)
  } catch {
    return url
  }
}
